import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { ThemeHelper } from '../helper/theme.helper';
import { Title } from '@angular/platform-browser';
import { updatePrimaryPalette } from '@primeuix/themes';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private _themeData = new BehaviorSubject<ThemeHelper | null>(null);
    readonly themeData = this._themeData.asObservable();
    private loadInFlight: Promise<void> | null = null;

    constructor(
        private authApiService: AuthApiService,
        private titleService: Title
    ) {
        this.getData();
    }

    public getData(): Promise<void> {
        if (!this.loadInFlight) {
            this.loadInFlight = this.fetchData().finally(() => {
                this.loadInFlight = null;
            });
        }
        return this.loadInFlight;
    }

    private async fetchData(): Promise<void> {
        const themeApiData = await firstValueFrom(this.authApiService.getThemeData()).catch(() => {
            return undefined;
        });

        const theme = new ThemeHelper();

        if (!themeApiData) {
            document.getElementById('missing-theme-container')?.classList.remove('d-none');

            theme.theme_available = false;
            this._themeData.next(theme);
        } else {
            theme.theme_accent_color = themeApiData.find((x: any) => x.key === 'theme_accent_color')?.value || '#ff0000';
            theme.theme_dark_accent_color = themeApiData.find((x: any) => x.key === 'theme_dark_accent_color')?.value || '#ff0000';
            theme.theme_dark_mode = themeApiData.find((x: any) => x.key === 'dark_mode')?.valueBoolean || false;
            theme.theme_title = themeApiData.find((x: any) => x.key === 'theme_title')?.value || 'CMP';
            theme.theme_logo = themeApiData.find((x: any) => x.key === 'theme_logo')?.value || '';
            theme.theme_logo_dark = themeApiData.find((x: any) => x.key === 'theme_logo_dark')?.value || '';
            theme.theme_slogan = themeApiData.find((x: any) => x.key === 'theme_slogan')?.value || 'Company Management Platform';
            theme.theme_favicon = themeApiData.find((x: any) => x.key === 'theme_favicon')?.value || '';
            theme.theme_motd_visible = themeApiData.find((x: any) => x.key === 'theme_motd_visible')?.valueBoolean || false;
            theme.theme_motd_text = themeApiData.find((x: any) => x.key === 'theme_motd_text')?.value || '';
            theme.theme_motd_color = themeApiData.find((x: any) => x.key === 'theme_motd_color')?.value || '#ff0000';
            theme.theme_staging = themeApiData.find((x: any) => x.key === 'theme_staging')?.valueBoolean || false;
            theme.theme_available = true;
            this.titleService.setTitle(theme.theme_title);
            this._themeData.next(theme);

            document.documentElement.style.setProperty('--theme-accent-color', theme.theme_accent_color);
            document.documentElement.style.setProperty('--theme-dark-accent-color', theme.theme_dark_accent_color);

            updatePrimaryPalette(this.buildPrimaryPalette(theme.theme_accent_color));
        }
    }

    public async updateTitle(title: string): Promise<any> {
        const themeTitle = this._themeData.value?.theme_title;
        this.titleService.setTitle(themeTitle ? `${title} | ${themeTitle}` : title);
    }

    private buildPrimaryPalette(accent: string): Record<number, string> {
        const base = accent.startsWith('#') ? accent : `#${accent}`;
        const darker = this.generateDarkerColors(base, 5);
        return {
            50: this.mixColor(base, '#ffffff', 0.92),
            100: this.mixColor(base, '#ffffff', 0.84),
            200: this.mixColor(base, '#ffffff', 0.68),
            300: this.mixColor(base, '#ffffff', 0.52),
            400: this.mixColor(base, '#ffffff', 0.36),
            500: base,
            600: darker[0],
            700: darker[1],
            800: darker[2],
            900: darker[3],
            950: darker[4]
        };
    }

    private mixColor(hex: string, mixHex: string, mixWeight: number): string {
        const parse = (value: string) => {
            const num = parseInt(value.slice(1), 16);
            return { r: num >> 16, g: (num >> 8) & 0xff, b: num & 0xff };
        };
        const a = parse(hex);
        const b = parse(mixHex);
        const weight = Math.min(Math.max(mixWeight, 0), 1);
        const channel = (from: number, to: number) =>
            Math.round(from * (1 - weight) + to * weight);
        const r = channel(a.r, b.r);
        const g = channel(a.g, b.g);
        const blue = channel(a.b, b.b);
        return (
            '#' +
            [r, g, blue]
                .map((value) => value.toString(16).padStart(2, '0'))
                .join('')
                .toUpperCase()
        );
    }

    generateDarkerColors(hex: string, count: number = 10) {
        const darkenColor = (hex: string, percent: number) => {
            let num = parseInt(hex.slice(1), 16),
                r = num >> 16,
                g = (num >> 8) & 0x00ff,
                b = num & 0x0000ff;

            r = Math.max(0, Math.floor(r * (1 - percent)));
            g = Math.max(0, Math.floor(g * (1 - percent)));
            b = Math.max(0, Math.floor(b * (1 - percent)));

            return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
        };

        let colors = [];
        for (let i = 1; i <= count; i++) {
            colors.push(darkenColor(hex, i / (count + 1)));
        }
        return colors;
    }
}
